// TaxIDCollection.tsx
import Button from 'components/button/Button';
import FormTextField from 'components/form/FormTextField';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import * as Yup from 'yup';

type Props = {
    onComplete: () => void;
    onUpdateTaxID: (taxId: string) => Promise<boolean>;
};

const validationSchema = Yup.object({
    taxId: Yup.string()
        .required('Tax ID / SSN is required')
        .matches(/^\d{9}$/, 'Tax ID / SSN must be exactly 9 digits')
        .test('is-nine-digits', 'Tax ID / SSN must be exactly 9 digits', (value) => {
            return value ? value.length === 9 : false;
        }),
});

/**
 * Tax ID / SSN collection step shown before acknowledgements for DDA customers
 */
export const TaxIDCollection = ({ onComplete, onUpdateTaxID }: Props) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const initialValues = {
        taxId: '',
    };

    const handleSubmit = async (values: typeof initialValues) => {
        setIsSubmitting(true);
        setErrorMessage(null);

        try {
            const success = await onUpdateTaxID(values.taxId);
            
            if (success) {
                onComplete();
            } else {
                setErrorMessage('Failed to update Tax ID. Please try again.');
            }
        } catch (error) {
            setErrorMessage('An error occurred. Please try again.');
            console.error('Error updating Tax ID:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="ConsentCollection">
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ errors, touched, values }) => {
                    const isValid = values.taxId.length === 9 && !errors.taxId;

                    return (
                        <Form>
                            <h2>Tax Identification Number</h2>
                            <p className="FormSubtitle">
                                Please provide your Tax ID / SSN to proceed with account verification.
                                This is required for DDA account creation.
                            </p>

                            {errorMessage && (
                                <div className="ErrorMessage" style={{ marginBottom: '20px' }}>
                                    {errorMessage}
                                </div>
                            )}

                            <div className="FormBox">
                                <div className="FormLabel">
                                    
                                </div>
                                <div className="FormField">
                                    <FormTextField
                                        field="taxId"
                                        label="Tax ID / SSN *"
                                        placeholder="Enter 9-digit Tax ID / SSN"
                                        type="text"
                                        maxLength={9}
                                        autoComplete={false}
                                    />
                                    {touched.taxId && errors.taxId && (
                                        <div className="FieldError">{errors.taxId}</div>
                                    )}
                                    <div className="FieldHint">
                                        Enter exactly 9 digits (e.g., 123456789)
                                    </div>
                                </div>
                            </div>

                            <div className="FormBox" style={{ marginTop: '20px' }}>
                                <div className="FormLabel">
                                    <label>Privacy Notice</label>
                                </div>
                                <div className="FormField">
                                    <p style={{ fontSize: '14px', color: '#666' }}>
                                        Your Tax ID / SSN is collected to meet compliance requirements and will be securely stored and used only for verification purposes.
                                    </p>
                                </div>
                            </div>

                            <Button
                                type="submit"
                                variety="full"
                                disabled={!isValid || isSubmitting}
                                showSubmittingSpinner={isSubmitting}
                            >
                                {isSubmitting ? 'Saving...' : 'Continue'}
                            </Button>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};